/**
 * Minimal IndexedDB-backed offline punch queue (PWA, plan §9.3).
 *
 * When a punch POST fails while the browser is offline, the punch body is
 * stored here keyed by its client_punch_uuid. On reconnect the queue is
 * flushed to POST /attendance/punch/batch; the server dedups on
 * client_punch_uuid so a double-flush (e.g. Background Sync + the foreground
 * `online` listener both firing) is a no-op.
 *
 * No external dependency (CLAUDE.md: no new top-level deps) — raw IndexedDB,
 * wrapped in promises. Degrades gracefully when IndexedDB is unavailable
 * (in-memory fallback so SSR / private-mode browsers don't throw).
 */

import type { PunchRequest } from "@/types/attendance";

const DB_NAME = "workright-attendance";
const STORE = "punch-queue";
const DB_VERSION = 1;

export interface QueuedPunch {
  client_punch_uuid: string;
  body: PunchRequest;
  queued_at: number;
}

// In-memory fallback when IndexedDB is unavailable (SSR, private mode).
const memFallback = new Map<string, QueuedPunch>();

function hasIDB(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "client_punch_uuid" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueuePunch(body: PunchRequest): Promise<void> {
  const item: QueuedPunch = {
    client_punch_uuid: body.client_punch_uuid,
    body,
    queued_at: Date.now(),
  };
  if (!hasIDB()) {
    memFallback.set(item.client_punch_uuid, item);
    return;
  }
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function dequeueAll(): Promise<QueuedPunch[]> {
  if (!hasIDB()) {
    return [...memFallback.values()];
  }
  const db = await openDB();
  const out = await new Promise<QueuedPunch[]>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as QueuedPunch[]);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return out;
}

export async function removeFromQueue(uuid: string): Promise<void> {
  if (!hasIDB()) {
    memFallback.delete(uuid);
    return;
  }
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(uuid);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function queueSize(): Promise<number> {
  return (await dequeueAll()).length;
}

/** newPunchUUID returns a client-generated UUID for a punch. Falls back to a
 *  timestamp+random string when crypto.randomUUID is unavailable. */
export function newPunchUUID(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
